import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Progress } from './ui/progress';
import { 
  Building2,
  MapPin,
  Globe,
  Mail,
  Phone,
  Award,
  Search,
  TrendingUp,
  Eye,
  ExternalLink,
  SlidersHorizontal,
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  X,
  Download
} from 'lucide-react';
import { toast } from 'sonner';

const API = BACKEND_URL;

const PublicRegistry = () => {
  const [actors, setActors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedActor, setSelectedActor] = useState(null);
  const [filterCertification, setFilterCertification] = useState('all');
  const [filterOrgType, setFilterOrgType] = useState('all');
  const [filterSDG, setFilterSDG] = useState('all');
  const [filterCity, setFilterCity] = useState('all');
  const [sortBy, setSortBy] = useState('name_asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  useEffect(() => {
    fetchPublicActors();
  }, []);

  const fetchPublicActors = async () => {
    try {
      setLoading(true);
      // Use the public endpoint that doesn't require authentication
      const response = await axios.get(`${API}/api/social-actors/public`);
      setActors(response.data);
    } catch (error) {
      console.error('Failed to fetch actors:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateCompleteness = (actor) => {
    let totalFields = 0;
    let filledFields = 0;

    const priority1Fields = [
      'organization_name', 'organization_type', 'legal_status',
      'legal_form', 'tax_id', 'email', 'social_mission'
    ];
    
    priority1Fields.forEach(field => {
      totalFields += 20;
      if (actor[field] && actor[field] !== '') filledFields += 20;
    });

    if (actor.organizational_indicators || actor.impact_indicators || actor.investment_readiness) {
      totalFields += 10;
      const hasAssessment = 
        (actor.organizational_indicators && Object.keys(actor.organizational_indicators).length > 0) ||
        (actor.impact_indicators && Object.keys(actor.impact_indicators).length > 0) ||
        (actor.investment_readiness && Object.keys(actor.investment_readiness).length > 0);
      if (hasAssessment) filledFields += 10;
    }

    totalFields += 10;
    if (actor.documents && actor.documents.length > 0) filledFields += 10;

    const percentage = totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;
    return { percentage, filledFields, totalFields };
  };

  const getCertificationBadge = (level) => {
    if (!level) return null;
    
    const badges = {
      level_3_excellence: {
        label: 'Level A - Excellence',
        color: 'bg-gradient-to-r from-yellow-400 to-amber-500',
        textColor: 'text-white',
        icon: '🏆'
      },
      level_2_commitment: {
        label: 'Level B - Commitment',
        color: 'bg-gradient-to-r from-blue-400 to-blue-600',
        textColor: 'text-white',
        icon: '⭐'
      },
      level_1_basic: {
        label: 'Level C - Basic',
        color: 'bg-gradient-to-r from-green-400 to-green-600',
        textColor: 'text-white',
        icon: '✓'
      }
    };

    const badge = badges[level] || badges.level_1_basic;
    
    return (
      <Badge className={`${badge.color} ${badge.textColor} font-semibold px-3 py-1`}>
        {badge.icon} {badge.label}
      </Badge>
    );
  };

  // Get unique cities from actors for the city filter dropdown
  const uniqueCities = [...new Set(actors.filter(a => a.city).map(a => a.city))].sort();

  // SDG options for filter
  const sdgOptions = Array.from({ length: 17 }, (_, i) => i + 1);

  // Organization type options
  const orgTypeOptions = [
    { value: 'social_enterprise', label: 'Social Enterprise' },
    { value: 'non_profit', label: 'Non-Profit' },
    { value: 'cooperative', label: 'Cooperative' },
    { value: 'ngo', label: 'NGO' },
    { value: 'other', label: 'Other' }
  ];

  // Sorting options
  const sortOptions = [
    { value: 'name_asc', label: 'Name (A-Z)' },
    { value: 'name_desc', label: 'Name (Z-A)' },
    { value: 'score_high', label: 'Score (High to Low)' },
    { value: 'score_low', label: 'Score (Low to High)' },
    { value: 'completeness_high', label: 'Completeness (High to Low)' },
    { value: 'completeness_low', label: 'Completeness (Low to High)' }
  ];

  // Clear all filters
  const clearAllFilters = () => {
    setFilterCertification('all');
    setFilterOrgType('all');
    setFilterSDG('all');
    setFilterCity('all');
    setSortBy('name_asc');
    setSearchQuery('');
  };

  // Count active filters
  const activeFilterCount = [
    filterCertification !== 'all',
    filterOrgType !== 'all',
    filterSDG !== 'all',
    filterCity !== 'all'
  ].filter(Boolean).length;

  // Filter and sort actors
  const filteredActors = actors
    .filter(actor => {
      // Search query filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = (
          actor.organization_name?.toLowerCase().includes(query) ||
          actor.city?.toLowerCase().includes(query) ||
          actor.social_mission?.toLowerCase().includes(query) ||
          actor.organization_type?.toLowerCase().includes(query)
        );
        if (!matchesSearch) return false;
      }

      // Certification filter
      if (filterCertification !== 'all' && actor.certification_level !== filterCertification) {
        return false;
      }

      // Organization type filter
      if (filterOrgType !== 'all' && actor.organization_type !== filterOrgType) {
        return false;
      }

      // SDG filter
      if (filterSDG !== 'all') {
        const sdgNum = parseInt(filterSDG);
        if (!actor.sdg_alignment?.includes(sdgNum)) {
          return false;
        }
      }

      // City filter
      if (filterCity !== 'all' && actor.city !== filterCity) {
        return false;
      }

      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name_asc':
          return (a.organization_name || '').localeCompare(b.organization_name || '');
        case 'name_desc':
          return (b.organization_name || '').localeCompare(a.organization_name || '');
        case 'score_high':
          return (b.assessment_scores?.overall || 0) - (a.assessment_scores?.overall || 0);
        case 'score_low':
          return (a.assessment_scores?.overall || 0) - (b.assessment_scores?.overall || 0);
        case 'completeness_high':
          return calculateCompleteness(b).percentage - calculateCompleteness(a).percentage;
        case 'completeness_low':
          return calculateCompleteness(a).percentage - calculateCompleteness(b).percentage;
        default:
          return 0;
      }
    });

  const viewActorDetails = (actor) => {
    setSelectedActor(actor);
    setShowDetailDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-xl font-semibold text-gray-700">Loading certified organizations...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Certified Social Actors Registry
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-2">
              Discover verified social enterprises committed to making a positive impact
            </p>
            <p className="text-lg text-blue-200">
              {actors.length} Certified Organizations
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8 space-y-6">
        {/* Search and Filters */}
        <Card>
          <CardContent className="pt-6">
            {/* Primary Filters Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <Label>Search Organizations</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input
                    placeholder="Search by name, city, mission..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div>
                <Label>Sort By</Label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger>
                    <ArrowUpDown className="h-4 w-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sortOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-end">
                <Button
                  variant={showAdvancedFilters ? "default" : "outline"}
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className="w-full"
                >
                  <SlidersHorizontal className="h-4 w-4 mr-2" />
                  Filters
                  {activeFilterCount > 0 && (
                    <Badge variant="secondary" className="ml-2 bg-blue-100 text-blue-700">
                      {activeFilterCount}
                    </Badge>
                  )}
                  {showAdvancedFilters ? (
                    <ChevronUp className="h-4 w-4 ml-1" />
                  ) : (
                    <ChevronDown className="h-4 w-4 ml-1" />
                  )}
                </Button>
              </div>
            </div>

            {/* Advanced Filters (Collapsible) */}
            {showAdvancedFilters && (
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-600">Advanced Filters</span>
                  {activeFilterCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearAllFilters}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="h-3 w-3 mr-1" />
                      Clear All
                    </Button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Organization Type</Label>
                    <Select value={filterOrgType} onValueChange={setFilterOrgType}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        {orgTypeOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>SDG Alignment</Label>
                    <Select value={filterSDG} onValueChange={setFilterSDG}>
                      <SelectTrigger>
                        <SelectValue placeholder="All SDGs" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All SDGs</SelectItem>
                        {sdgOptions.map(sdg => (
                          <SelectItem key={sdg} value={sdg.toString()}>
                            SDG {sdg}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Certification Level</Label>
                    <Select value={filterCertification} onValueChange={setFilterCertification}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Levels</SelectItem>
                        <SelectItem value="level_3_excellence">🏆 Level A - Excellence</SelectItem>
                        <SelectItem value="level_2_commitment">⭐ Level B - Commitment</SelectItem>
                        <SelectItem value="level_1_basic">✓ Level C - Basic</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>City</Label>
                    <Select value={filterCity} onValueChange={setFilterCity}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Cities" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Cities</SelectItem>
                        {uniqueCities.map(city => (
                          <SelectItem key={city} value={city}>
                            {city}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Results Summary */}
            <div className="mt-4 pt-4 border-t flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing <span className="font-semibold">{filteredActors.length}</span> of{' '}
                <span className="font-semibold">{actors.length}</span> certified organizations
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Organizations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredActors.length === 0 ? (
            <div className="col-span-3 text-center py-12">
              <div className="text-gray-500 text-lg">
                {searchQuery ? `No organizations found matching "${searchQuery}"` : 'No certified organizations available'}
              </div>
            </div>
          ) : (
            filteredActors.map((actor) => {
              const completeness = calculateCompleteness(actor);
              
              return (
                <Card key={actor.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg font-bold text-gray-900 mb-2">
                          {actor.organization_name}
                        </CardTitle>
                        {actor.certification_level && (
                          <div className="mb-2">
                            {getCertificationBadge(actor.certification_level)}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-3">
                    <div className="flex items-center text-sm text-gray-600">
                      <Building2 className="h-4 w-4 mr-2" />
                      <span className="capitalize">{actor.organization_type?.replace('_', ' ')}</span>
                    </div>
                    
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="h-4 w-4 mr-2" />
                      <span>{actor.city}, {actor.country}</span>
                    </div>
                    
                    {actor.website && (
                      <div className="flex items-center text-sm text-blue-600">
                        <Globe className="h-4 w-4 mr-2" />
                        <a href={actor.website} target="_blank" rel="noopener noreferrer" className="hover:underline">
                          Visit Website
                        </a>
                      </div>
                    )}
                    
                    <div className="text-sm text-gray-700 line-clamp-2">
                      {actor.social_mission}
                    </div>
                    
                    {actor.sdg_alignment && actor.sdg_alignment.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {actor.sdg_alignment.slice(0, 3).map(sdg => (
                          <Badge key={sdg} variant="outline" className="text-xs">
                            SDG {sdg}
                          </Badge>
                        ))}
                        {actor.sdg_alignment.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{actor.sdg_alignment.length - 3} more
                          </Badge>
                        )}
                      </div>
                    )}
                    
                    {/* Profile Completeness */}
                    <div className="pt-2 border-t">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-600 flex items-center gap-1">
                          <TrendingUp className="h-3 w-3" />
                          Profile Completeness
                        </span>
                        <span className="text-xs font-semibold text-gray-700">
                          {completeness.percentage}%
                        </span>
                      </div>
                      <Progress value={completeness.percentage} className="h-2" />
                    </div>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => viewActorDetails(actor)}
                      className="w-full mt-2"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>

      {/* Detail Dialog */}
      {selectedActor && (
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl">{selectedActor.organization_name}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              <div className="flex gap-2 flex-wrap">
                {selectedActor.certification_level && getCertificationBadge(selectedActor.certification_level)}
                <Badge variant="outline" className="capitalize">
                  {selectedActor.status}
                </Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600">Organization Type</Label>
                  <p className="capitalize">{selectedActor.organization_type?.replace('_', ' ')}</p>
                </div>
                
                <div>
                  <Label className="text-gray-600">Legal Status</Label>
                  <p>{selectedActor.legal_status}</p>
                </div>
                
                {selectedActor.legal_form && (
                  <div>
                    <Label className="text-gray-600">Legal Form</Label>
                    <p>{selectedActor.legal_form}</p>
                  </div>
                )}
                
                {selectedActor.year_established && (
                  <div>
                    <Label className="text-gray-600">Year Established</Label>
                    <p>{selectedActor.year_established}</p>
                  </div>
                )}
                
                <div>
                  <Label className="text-gray-600">Location</Label>
                  <p>{selectedActor.city}, {selectedActor.country}</p>
                </div>
                
                {selectedActor.address && (
                  <div className="col-span-2">
                    <Label className="text-gray-600">Address</Label>
                    <p>{selectedActor.address}</p>
                  </div>
                )}
                
                <div>
                  <Label className="text-gray-600">Email</Label>
                  <p>{selectedActor.email}</p>
                </div>
                
                {selectedActor.phone && (
                  <div>
                    <Label className="text-gray-600">Phone</Label>
                    <p>{selectedActor.phone}</p>
                  </div>
                )}
                
                {selectedActor.website && (
                  <div>
                    <Label className="text-gray-600">Website</Label>
                    <a 
                      href={selectedActor.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-1"
                    >
                      {selectedActor.website}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                )}
              </div>
              
              <div>
                <Label className="text-gray-600">Social Mission</Label>
                <p className="text-gray-800">{selectedActor.social_mission}</p>
              </div>
              
              {selectedActor.sdg_alignment && selectedActor.sdg_alignment.length > 0 && (
                <div>
                  <Label className="text-gray-600">SDG Alignment</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedActor.sdg_alignment.map(sdg => (
                      <Badge key={sdg} variant="outline">
                        SDG {sdg}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Assessment Scores if available */}
              {selectedActor.assessment_scores && Object.keys(selectedActor.assessment_scores).length > 0 && (
                <div className="border-t pt-4">
                  <Label className="text-gray-600 text-lg mb-3 block">Assessment Scores</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-blue-50 rounded">
                      <div className="text-2xl font-bold text-blue-600">
                        {selectedActor.assessment_scores.overall || 0}%
                      </div>
                      <div className="text-sm text-gray-600">Overall</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded">
                      <div className="text-xl font-semibold text-green-700">
                        {selectedActor.assessment_scores.strategy || 0}%
                      </div>
                      <div className="text-xs text-gray-600">Strategy</div>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded">
                      <div className="text-xl font-semibold text-purple-600">
                        {selectedActor.assessment_scores.governance || 0}%
                      </div>
                      <div className="text-xs text-gray-600">Governance</div>
                    </div>
                    <div className="text-center p-3 bg-orange-50 rounded">
                      <div className="text-xl font-semibold text-orange-700">
                        {selectedActor.assessment_scores.impact || 0}%
                      </div>
                      <div className="text-xs text-gray-600">Impact</div>
                    </div>
                    <div className="text-center p-3 bg-teal-50 rounded">
                      <div className="text-xl font-semibold text-teal-700">
                        {selectedActor.assessment_scores.transparency || 0}%
                      </div>
                      <div className="text-xs text-gray-600">Transparency</div>
                    </div>
                    <div className="text-center p-3 bg-indigo-50 rounded">
                      <div className="text-xl font-semibold text-indigo-600">
                        {selectedActor.assessment_scores.investment || 0}%
                      </div>
                      <div className="text-xs text-gray-600">Investment</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default PublicRegistry;
